"use client";

import {
  type AcquisitionDemoFramePhase,
  createAcquisitionDemoFrameResizeMessage,
  getAcquisitionDemoFrameHeight,
} from "@/lib/acquisition-demo/frame-bridge";
import { useEffect } from "react";

const CONFIGURABLE_ENTRY_SELECTOR = '[data-pre-session-variant="configurable-start"]';

export function AcquisitionDemoFrameReporter() {
  useEffect(() => {
    if (window.parent === window) return;

    let animationFrame: number | null = null;
    let lastReport: string | null = null;

    const reportFrameSize = () => {
      animationFrame = null;
      const configurableEntry = document.querySelector<HTMLElement>(CONFIGURABLE_ENTRY_SELECTOR);
      const phase: AcquisitionDemoFramePhase = configurableEntry ? "pre_session" : "active";
      const height = getAcquisitionDemoFrameHeight({
        phase,
        viewportHeight: window.innerHeight,
        contentBottom: configurableEntry?.getBoundingClientRect().bottom,
        scrollOffset: window.scrollY,
      });
      const reportKey = `${phase}:${height}`;
      if (reportKey === lastReport) return;

      lastReport = reportKey;
      window.parent.postMessage(
        createAcquisitionDemoFrameResizeMessage(phase, height),
        window.location.origin
      );
    };

    const scheduleFrameSizeReport = () => {
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(reportFrameSize);
    };

    const resizeObserver =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(scheduleFrameSizeReport);
    resizeObserver?.observe(document.documentElement);
    if (document.body) resizeObserver?.observe(document.body);

    const mutationObserver =
      typeof MutationObserver === "undefined"
        ? null
        : new MutationObserver(scheduleFrameSizeReport);
    if (document.body) {
      mutationObserver?.observe(document.body, { childList: true, subtree: true });
    }

    window.addEventListener("resize", scheduleFrameSizeReport);
    scheduleFrameSizeReport();

    return () => {
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener("resize", scheduleFrameSizeReport);
    };
  }, []);

  return null;
}
